/**
 * Best-effort metered-connection detection (Network Information API).
 *
 * Browser support is limited and privacy-restricted: `saveData` (Data Saver) is
 * the widely-supported, reliable signal; `type === "cellular"` exists on some
 * Android browsers but is removed elsewhere. So we only treat a connection as
 * metered when we can positively detect it — otherwise we assume unmetered and
 * keep uploading. Document this limitation to operators.
 */

interface NetworkInfoLike {
  saveData?: boolean;
  type?: string;
  /** "slow-2g" | "2g" | "3g" | "4g" — coarse speed class (Android/Chromium). */
  effectiveType?: string;
  /** Estimated downlink bandwidth in Mbps. */
  downlink?: number;
  /** Estimated round-trip time in ms. */
  rtt?: number;
  addEventListener?: (t: string, cb: () => void) => void;
  removeEventListener?: (t: string, cb: () => void) => void;
}

function connection(): NetworkInfoLike | undefined {
  return (navigator as unknown as { connection?: NetworkInfoLike }).connection;
}

/** True only when we can positively detect a metered/data-saver connection. */
export function isMetered(): boolean {
  const c = connection();
  if (!c) return false; // can't detect → assume unmetered
  if (c.saveData === true) return true;
  if (typeof c.type === "string") return c.type === "cellular" || c.type === "wimax";
  return false;
}

export type UploadPolicy = "always" | "wifi-only";

/**
 * Call `onChange(uploadEnabled)` now and whenever the connection changes.
 * `wifi-only` disables upload on detected metered links; `always` never disables.
 * Returns an unsubscribe function.
 */
export function watchUpload(policy: UploadPolicy, onChange: (uploadEnabled: boolean) => void): () => void {
  const evaluate = () => onChange(policy === "always" ? true : !isMetered());
  evaluate();
  const c = connection();
  if (policy === "wifi-only" && c?.addEventListener) {
    c.addEventListener("change", evaluate);
    return () => c.removeEventListener?.("change", evaluate);
  }
  return () => {};
}

export interface AdaptiveKnobs {
  maxPeers: number;
  prefetchAhead: number;
  maxConcurrentPrefetch: number;
  // Anti-rebuffer knobs: on a weak link, fall back to the CDN sooner.
  httpOnlyDeadlineMs: number;
  hedgeAtDeadlineFraction: number;
  peerRequestTimeoutMs: number;
  /** Skip a measurably-too-slow peer straight to CDN (0 = off). */
  maxPeerRttMs: number;
}

/**
 * Effective speed class from the full connection signal — NOT just `effectiveType`.
 * The Network Info API has no "5g" (5G and good 4G both report "4g"), and a
 * congested 4G still reports "4g"; so a "4g"/unknown link with low `downlink` or
 * high `rtt` is treated as 3g so it still gets the anti-rebuffer profile.
 */
export function deriveClass(c: NetworkInfoLike | undefined): string | undefined {
  const et = c?.effectiveType;
  if (et === "slow-2g" || et === "2g") return "2g";
  if (et === "3g") return "3g";
  const poorDownlink = typeof c?.downlink === "number" && c.downlink > 0 && c.downlink < 1.5; // Mbps
  const poorRtt = typeof c?.rtt === "number" && c.rtt > 300; // ms
  if (poorDownlink || poorRtt) return "3g"; // congested 4g/5g → treat as 3g
  return et; // "4g" or undefined
}

/**
 * Adapt the baseline knobs for a constrained link. Mesh knobs (peers/prefetch) are
 * REDUCED; the anti-rebuffer knobs shift so the engine leans on the CDN earlier
 * (skip P2P further from the deadline, hedge sooner, drop a slow peer faster). 4g/
 * unknown returns `base` unchanged, so this only tightens on weak networks and never
 * loosens beyond the operator's central config (no tug-of-war with the /config poll).
 */
export function capForEffectiveType(base: AdaptiveKnobs, effectiveType?: string): AdaptiveKnobs {
  const profile =
    effectiveType === "slow-2g" || effectiveType === "2g"
      ? { maxPeers: 3, prefetchAhead: 4, maxConcurrentPrefetch: 2, httpOnlyDeadlineMs: 5000, hedgeAtDeadlineFraction: 0.25, peerRequestTimeoutMs: 2000, maxPeerRttMs: 250 }
      : effectiveType === "3g"
      ? { maxPeers: 6, prefetchAhead: 8, maxConcurrentPrefetch: 3, httpOnlyDeadlineMs: 3500, hedgeAtDeadlineFraction: 0.35, peerRequestTimeoutMs: 2500, maxPeerRttMs: 400 }
      : null;
  if (!profile) return base; // 4g / unknown → restore the baseline
  return {
    maxPeers: Math.min(base.maxPeers, profile.maxPeers),
    prefetchAhead: Math.min(base.prefetchAhead, profile.prefetchAhead),
    maxConcurrentPrefetch: Math.min(base.maxConcurrentPrefetch, profile.maxConcurrentPrefetch),
    // Deadline: LARGER = skip P2P sooner → take the higher (more CDN-biased) value.
    httpOnlyDeadlineMs: Math.max(base.httpOnlyDeadlineMs, profile.httpOnlyDeadlineMs),
    // Hedge/timeout: SMALLER = fall back sooner → take the lower value.
    hedgeAtDeadlineFraction: Math.min(base.hedgeAtDeadlineFraction, profile.hedgeAtDeadlineFraction),
    peerRequestTimeoutMs: Math.min(base.peerRequestTimeoutMs, profile.peerRequestTimeoutMs),
    // RTT gate: enable on weak links; if the operator already set a tighter (nonzero)
    // gate keep the smaller, else use the profile's.
    maxPeerRttMs: base.maxPeerRttMs > 0 ? Math.min(base.maxPeerRttMs, profile.maxPeerRttMs) : profile.maxPeerRttMs,
  };
}

/**
 * Call `apply(knobs)` now and on every connection change, scaling mesh
 * aggressiveness DOWN from `base` on a constrained link (a slow 3G shouldn't hold
 * 10 peers + a deep prefetch window). Pairs with `engine.updateConfig`.
 */
export function watchConnection(base: AdaptiveKnobs, apply: (knobs: AdaptiveKnobs) => void): () => void {
  const evaluate = () => apply(capForEffectiveType(base, deriveClass(connection())));
  evaluate();
  const c = connection();
  if (c?.addEventListener) {
    c.addEventListener("change", evaluate);
    return () => c.removeEventListener?.("change", evaluate);
  }
  return () => {};
}
